import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, ViewChild, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormBuilder, UntypedFormGroup, Validator } from '@angular/forms';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { DatePickerComponent } from './date-picker.component';
import { DatePickerConfig } from './date-picker.config';

const FORM_INPUT_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TimePickerComponent),
    multi: true
};

const FORM_INPUT_VALIDATOR = {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => TimePickerComponent),
    multi: true
};

/**
 * Eine Komponente, die stellt ein Date Picker und zwei Input-Felder (Stunden und Minuten) dar.
 */
@Component({
    selector: 'common-time-picker',
    template: `
        <div class="commonTimepicker" [formGroup]="group">
            <common-date-picker class="commonTimepickerDate" formControlName="date"
                                [options]="options"></common-date-picker>
            <common-time-input formControlName="time"
                               [hourPlaceholder]="dateTo ? '23' : '00'"
                               [minutePlaceholder]="dateTo ? '59' : '00'"
                               ></common-time-input>
        </div>
    `,
    providers: [FORM_INPUT_CONTROL_VALUE_ACCESSOR, FORM_INPUT_VALIDATOR]
})
export class TimePickerComponent implements ControlValueAccessor, Validator, AfterViewInit {
    @ViewChild('dp') dp: DatePickerComponent;

    @Input() dateTo: boolean = false;

    @Input() options: DatePickerConfig;

    group: UntypedFormGroup;

    disabled: boolean = false;

    private touch = [];

    private validityChange: EventEmitter<any> = new EventEmitter<any>();

    private ngUnsubscribe$: Subject<void> = new Subject<void>();

    constructor(private fb: UntypedFormBuilder, private cd: ChangeDetectorRef) {
        this.group = this.fb.group({
            date: [],
            time: []
        });
    }

    ngAfterViewInit() {
        this.group.get('date').valueChanges.pipe(
            takeUntil(this.ngUnsubscribe$),
            filter(x => x === null || x === undefined)
        ).subscribe(() => this.group.reset(undefined, {emitEvent: false}));
    }

    clear() {
        this.group.reset();
    }

    writeValue(value: Date | string | number): void {
        // Updating a FormGroup element here does not cause an automatic change detection by Angular
        // Therefore, it leads to the NG0100 Angular error (https://angular.io/errors/NG0100)
        // To prevent this error, change detection must be triggered here manually by using #ChangeDetectorRef.detectChanges()
        if (value === undefined || value === null || value === '') {
            this.group.reset(undefined, {emitEvent: false});
            this.cd.detectChanges()
            return;
        }
        // Das Datum-Objekt kann Date, Iso String oder Int sein.
        // Date Format (dd.mm.jjjj) wird lediglich innerhalb Datepicker benutzt.
        const parsedDate = new Date(value);
        if (isNaN(parsedDate.getTime()) || !(parsedDate instanceof Date)) {
            return;
        }
        this.group.patchValue({
            date: parsedDate,
            time: parsedDate.getHours() + ':' + parsedDate.getMinutes() + ':' + parsedDate.getSeconds()
        }, {emitEvent: false});
        this.cd.detectChanges()
    }

    registerOnChange(fn: any): void {
        this.group.valueChanges.pipe(
            distinctUntilChanged(), // https://github.com/angular/angular/issues/12540
            map(x => this.prepareOutputValue(x))
        ).subscribe(fn);
    }

    registerOnTouched(fn: any): void {
        this.touch.push(fn);
    }

    setDisabledState(isDisabled: boolean) {
        if (isDisabled) {
            return this.group.disable({emitEvent: false});
        }
        this.group.enable({emitEvent: false});
    }

    validate(c: AbstractControl): { [p: string]: any } {
        if (this.group.valid) {
            return null;
        }

        return ['date', 'time'].reduce((errors, curr) => {
            if (!this.group.controls[curr].errors) {
                return errors;
            }

            errors[curr] = this.group.controls[curr].errors;

            return errors;
        }, {});
    }

    registerOnValidatorChange(fn: () => void) {
        this.validityChange.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(fn);
    }

    private prepareOutputValue(changes: TimePickerChangesValue): Date {
        if (!changes.date || !(changes.date instanceof Date)) {
            return undefined;
        }

        const value = new Date(changes.date.getTime());

        if (changes.time) {
            const time = changes.time.split(':');
            value.setHours(parseInt(time[0]), parseInt(time[1]), parseInt(time[2]), 0);
        }
        return value;
    }
}

type TimePickerChangesValue = {
    date: Date | undefined,
    time: string | undefined // hh:mm:ss
};
